// New-part modal — fired from PartsPage's "+ New part" button (or any
// caller that wants the full picker → basics flow inline). Two steps:
//
//   1. Type picker (5 cards from PART_TYPE_META, matching the v8 mockup's
//      Step B). When invoked with `initialType`, this step is skipped.
//   2. Basics — part number, description, qty tiers, optional notes.
//
// On submit: createPart({ ... }), then onCreated(id) — caller can route
// to the part detail page (whenever the detail-page agent lands). Like
// NewCustomerModal, the full edit experience (materials, machines,
// outside services, drawings, etc.) is deferred to the per-type wizard
// per spec/14. This modal only seeds the record.
//
// Field defaults follow `CreatePartInput` (Omit<Part, baseline>) so the
// part is valid the moment it hits Firebase — empty arrays for the
// collection fields, empty MaterialCost.perPiece, etc.

import { useEffect, useState } from 'react';
import { createPart } from '@/shared/crm';
import type { PartType } from '@/shared/crmTypes';
import { NumberInput } from './NumberInput';
import { PART_TYPE_META } from './PartTypeBadge';

export interface NewPartModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  /** Pre-select a part type (e.g. when called from a quote builder context
   *  that already knows the type). When set, the picker step is skipped. */
  initialType?: PartType;
}

// Tailwind needs literal class strings; map colorClass key → stripe bg.
const STRIPE_BG: Record<string, string> = {
  blue: 'bg-blue',
  purple: 'bg-purple',
  amber: 'bg-amber',
  green: 'bg-green',
  steel: 'bg-steel',
  teal: 'bg-teal',
};

const PICK_ORDER: PartType[] = ['machined', 'cut_blank', 'weldment', 'hybrid', 'collar', 'purchased', 'turninsulation'];

const DEFAULT_QTY_TIERS = [10, 50, 100] as const;

function parseQtyTiers(raw: string): number[] | null {
  // Accepts "10 / 50 / 100", "10,50,100", "10 50 100" — split on
  // non-digits, drop blanks, keep positive ints, allow 1-5 tiers.
  const nums = raw
    .split(/[^\d]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number.parseInt(s, 10));
  if (nums.length === 0 || nums.length > 5) return null;
  if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return null;
  return nums;
}

export function NewPartModal({ open, onClose, onCreated, initialType }: NewPartModalProps) {
  const [step, setStep] = useState<'pick' | 'basics'>(initialType ? 'basics' : 'pick');
  const [pickedType, setPickedType] = useState<PartType | null>(initialType ?? null);
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  // Single-qty by default — Justin opts into multi-tier via the checkbox.
  const [singleQty, setSingleQty] = useState(1);
  const [multiTier, setMultiTier] = useState(false);
  const [qtyTiersRaw, setQtyTiersRaw] = useState(DEFAULT_QTY_TIERS.join(' / '));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setStep(initialType ? 'basics' : 'pick');
      setPickedType(initialType ?? null);
      setNumber('');
      setDescription('');
      setSingleQty(1);
      setMultiTier(false);
      setQtyTiersRaw(DEFAULT_QTY_TIERS.join(' / '));
      setNotes('');
      setError(null);
      setBusy(false);
    }
  }, [open, initialType]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onClose]);

  if (!open) return null;

  function handlePick(type: PartType) {
    setPickedType(type);
    setStep('basics');
  }

  function handleBack() {
    // Only allow back when we entered via picker — otherwise the picker
    // step doesn't exist for this session.
    if (initialType) return;
    setStep('pick');
  }

  async function handleSubmit() {
    if (!pickedType) {
      setError('Pick a part type first.');
      return;
    }
    if (!number.trim()) {
      setError('Part number is required.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    let tiers: number[];
    if (multiTier) {
      const parsed = parseQtyTiers(qtyTiersRaw);
      if (!parsed) {
        setError('Qty tiers must be 1-5 positive integers (e.g. "10 / 50 / 100").');
        return;
      }
      tiers = parsed;
    } else {
      if (!Number.isFinite(singleQty) || singleQty <= 0) {
        setError('Quantity must be a positive integer.');
        return;
      }
      tiers = [Math.floor(singleQty)];
    }

    setBusy(true);
    setError(null);
    try {
      const id = await createPart({
        number: number.trim(),
        description: description.trim(),
        type: pickedType,
        qtyTiers: tiers,
        materialCost: { perPiece: [] },
        machines: [],
        dxfFiles: [],
        outsideServices: [],
        tooling: [],
        attachedDrawings: [],
        notes: notes.trim() || undefined,
      });
      onCreated?.(id);
      onClose();
    } catch (e) {
      setError(`Failed to create part: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  // ===== STEP 1: pick type =====
  if (step === 'pick') {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
        onClick={() => !busy && onClose()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-part-pick-title"
      >
        <div
          className="bg-paper rounded shadow-lg w-full max-w-[460px] max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-3 border-b border-line flex items-center justify-between bg-navy text-paper">
            <h2 id="new-part-pick-title" className="font-bold text-base">
              What kind of part is this?
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-paper/80 hover:text-paper text-lg leading-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="p-4">
            <p className="text-xs text-text-mute mb-3">
              Pick the type — that decides which sections you'll see in the next step.
            </p>

            <div className="flex flex-col gap-1.5">
              {PICK_ORDER.map((type) => {
                const meta = PART_TYPE_META[type];
                const stripeCls = STRIPE_BG[meta.colorClass] ?? 'bg-steel';
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handlePick(type)}
                    className="group flex items-stretch gap-3 border border-line rounded overflow-hidden text-left hover:border-orange hover:shadow-sm transition-all bg-paper"
                  >
                    <div className={`w-1.5 shrink-0 ${stripeCls}`} aria-hidden />
                    <div className="text-[22px] py-2 pl-1 shrink-0 self-center" aria-hidden>
                      {meta.icon}
                    </div>
                    <div className="flex-1 py-2 pr-3 min-w-0">
                      <div className="text-[13px] font-bold text-navy group-hover:text-orange transition-colors">
                        {meta.label}
                      </div>
                      <div className="text-[11px] text-text-mute mt-0.5">{meta.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Removed: pre-create "+ Drop a PDF/DXF to auto-detect type"
                button — it never had drag-drop handlers and Firebase Storage
                uploads need a saved partId to bind to. Drawing uploads now
                live on the wizard's Identity step (DrawingUpload component),
                where the part is already persisted. Auto-detect-by-AI lands
                in a future slice (would need a "deferred upload" mode on
                DrawingUpload that buffers File objects until a partId is
                provided). */}
          </div>
        </div>
      </div>
    );
  }

  // ===== STEP 2: basics =====
  const meta = pickedType ? PART_TYPE_META[pickedType] : null;
  const stripeCls = meta ? STRIPE_BG[meta.colorClass] ?? 'bg-steel' : 'bg-steel';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-part-basics-title"
    >
      <div
        className="bg-paper rounded shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 id="new-part-basics-title" className="font-bold text-navy text-lg">
            + New part
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-text-mute hover:text-navy disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {meta && (
          <div className="flex items-stretch gap-3 mx-5 mt-4 border border-line rounded overflow-hidden bg-bg/40">
            <div className={`w-1.5 shrink-0 ${stripeCls}`} aria-hidden />
            <div className="text-[22px] py-2 pl-1 shrink-0 self-center" aria-hidden>
              {meta.icon}
            </div>
            <div className="flex-1 py-2 pr-3 min-w-0">
              <div className="text-[13px] font-bold text-navy">{meta.label}</div>
              <div className="text-[11px] text-text-mute mt-0.5">{meta.description}</div>
            </div>
            {!initialType && (
              <button
                type="button"
                onClick={handleBack}
                disabled={busy}
                className="text-[11px] text-orange hover:underline pr-3 disabled:opacity-50"
              >
                change
              </button>
            )}
          </div>
        )}

        <div className="p-5 space-y-3">
          <Field label="Part number *">
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="BRK-2245-A"
              autoFocus
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>

          <Field label="Description *">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mounting bracket — Acme conveyor"
              rows={2}
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange resize-y"
            />
          </Field>

          {multiTier ? (
            <Field label="Qty tiers">
              <input
                type="text"
                value={qtyTiersRaw}
                onChange={(e) => setQtyTiersRaw(e.target.value)}
                placeholder="10 / 50 / 100"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
              <span className="block text-[11px] text-text-mute mt-1">
                1-5 positive integers, separated by slashes, commas, or spaces.
              </span>
            </Field>
          ) : (
            <Field label="Quantity">
              <NumberInput
                value={singleQty}
                onChange={(v) => setSingleQty(v === undefined ? 0 : Math.floor(v))}
                step={1}
                min={1}
                numpadTitle="Quantity"
              />
              <span className="block text-[11px] text-text-mute mt-1">
                Defaults to 1. Bump it up if you need a small run at this price.
              </span>
            </Field>
          )}

          <label className="flex items-center gap-2 text-xs text-text cursor-pointer select-none">
            <input
              type="checkbox"
              checked={multiTier}
              onChange={(e) => setMultiTier(e.target.checked)}
              className="accent-orange"
            />
            <span>Multi-tier production qty</span>
          </label>

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Material spec, finish notes, anything that helps later…"
              rows={2}
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange resize-y"
            />
          </Field>

          {error && (
            <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
              ⚠ {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-bg/50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !number.trim() || !description.trim()}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create part'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
